import * as React from 'react'
import Icon from 'components/styleguide/icon/Icon'

interface SeoInsightsPanelProps {
  pct_remote?: number | null
  salary_p10_eur?: number | null
  salary_p90_eur?: number | null
  top_framework?: string | null
  seniority_level?: string | null
  yoy_growth_pct?: number | null
  actual_edition?: number | null
  fallback?: boolean
}

interface StatRow {
  icon: string
  label: string
  value: string
}

const formatSalaryRange = (min: number, max: number): string => {
  const k = (v: number) => `€${Math.round(v / 1000)}k`
  return `${k(min)} - ${k(max)}`
}

const SeoInsightsPanel: React.FC<SeoInsightsPanelProps> = ({
  pct_remote,
  salary_p10_eur,
  salary_p90_eur,
  top_framework,
  seniority_level,
  yoy_growth_pct,
  actual_edition,
  fallback,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const formatGrowth = (value: number): string =>
    `${value >= 0 ? '+' : ''}${value}% Year over Year`

  const leftRows: StatRow[] = [
    pct_remote != null && {
      icon: 'globe',
      label: 'Remote & Hybrid Roles',
      value: `${pct_remote}%`,
    },
    salary_p10_eur != null && salary_p90_eur != null && {
      icon: 'dollar',
      label: 'Average Salary Range',
      value: formatSalaryRange(salary_p10_eur, salary_p90_eur),
    },
    seniority_level != null && seniority_level !== '' && {
      icon: 'barChart',
      label: 'Seniority Level',
      value: seniority_level,
    },
  ].filter(Boolean) as StatRow[]

  const editionLabel = fallback
    ? `${actual_edition} (prev. edition)`
    : String(actual_edition)

  const rightRows: StatRow[] = [
    yoy_growth_pct != null && {
      icon: 'trendingUp',
      label: 'Market Growth',
      value: formatGrowth(yoy_growth_pct),
    },
    top_framework != null && {
      icon: 'award',
      label: 'Most Requested Skill',
      value: top_framework,
    },
    {
      icon: 'stamp',
      label: 'Last Update',
      value: editionLabel,
    },
  ].filter(Boolean) as StatRow[]

  return (
    <>
      <button
        type="button"
        className="lj-searchHeader-insightsToggle"
        aria-expanded={isOpen}
        aria-controls="search-insights-panel"
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className="lj-searchHeader-insightsToggle-label">Market Insights</span>
        <span className="lj-searchHeader-insightsToggle-icon">
          <Icon
            name="chevronDown"
            size="sm"
            color="talent"
            aria-hidden={true}
          />
        </span>
      </button>

      {isOpen && (
        <div
          id="search-insights-panel"
          className="lj-searchHeader-insightsPanel"
          role="region"
          aria-label="Market Insights"
        >
          <div className="lj-insightsPanel-grid">
            {[leftRows, rightRows].map((column, colIdx) => (
              <div key={colIdx} className="lj-insightsPanel-column">
                {column.map(row => (
                  <div key={row.label} className="lj-insightRow">
                    <span className="lj-insightRow-labelGroup">
                      <Icon
                        name={row.icon}
                        size="sm"
                        color="neutral-text-base"
                        aria-hidden={true}
                      />
                      <span className="lj-insightRow-label">{row.label}</span>
                    </span>
                    <span className="lj-insightRow-value">{row.value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default SeoInsightsPanel
